import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ProjectManagementService } from '../project-management.service';

@Component({
  selector: 'app-project-risks',
  templateUrl: './project-risks.component.html',
  styleUrls: ['./project-risks.component.scss']
})
export class ProjectRisksComponent implements OnInit {
  loading = false;
  saving = false;
  projects: any[] = [];
  staffs: any[] = [];
  risks: any[] = [];
  selectedProjectId: number | null = null;
  selectedProject: any = null;
  search = '';
  statusFilter = 'all';
  severityFilter = 'all';
  editingRiskId: number | null = null;

  form = {
    title: '',
    description: '',
    severity: 'Medium',
    status: 'Open',
    mitigationPlan: '',
    ownerStaffId: null as number | null,
    targetResolutionDate: '',
    mentionedStaffIds: [] as number[]
  };

  readonly severityOptions = ['Low', 'Medium', 'High', 'Critical'];
  readonly statusOptions = ['Open', 'Monitoring', 'Mitigated', 'Closed'];

  constructor(private projectService: ProjectManagementService) {}

  ngOnInit(): void {
    this.loadContext();
  }

  loadContext(): void {
    this.loading = true;
    forkJoin({
      projects: this.projectService.getProjects(),
      staffs: this.projectService.getStaffs()
    }).subscribe({
      next: ({ projects, staffs }) => {
        this.projects = projects || [];
        this.staffs = staffs || [];
        this.selectedProjectId = this.projects[0]?.id || null;
        this.loadProjectRisks();
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load risk management workspace.', 'error');
      }
    });
  }

  loadProjectRisks(): void {
    if (!this.selectedProjectId) {
      this.loading = false;
      this.selectedProject = null;
      this.risks = [];
      return;
    }

    this.loading = true;
    forkJoin({
      project: this.projectService.getProject(this.selectedProjectId),
      risks: this.projectService.getProjectRisks(this.selectedProjectId)
    }).subscribe({
      next: ({ project, risks }) => {
        this.selectedProject = project || null;
        this.risks = risks || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load project risks.', 'error');
      }
    });
  }

  get filteredRisks(): any[] {
    const term = this.search.trim().toLowerCase();
    return this.risks.filter((risk) => {
      const matchesTerm = !term || [risk.title, risk.description, risk.ownerStaffName, risk.mitigationPlan]
        .some((value) => String(value || '').toLowerCase().includes(term));
      const matchesStatus = this.statusFilter === 'all' || String(risk.status || '').toLowerCase() === this.statusFilter.toLowerCase();
      const matchesSeverity = this.severityFilter === 'all' || String(risk.severity || '').toLowerCase() === this.severityFilter.toLowerCase();
      return matchesTerm && matchesStatus && matchesSeverity;
    });
  }

  get stats(): Array<{ label: string; value: number; tone: string }> {
    return [
      { label: 'Open', value: this.countByStatus('Open'), tone: 'danger' },
      { label: 'Monitoring', value: this.countByStatus('Monitoring'), tone: 'warning' },
      { label: 'Mitigated', value: this.countByStatus('Mitigated'), tone: 'success' },
      { label: 'Critical', value: this.countBySeverity('Critical'), tone: 'critical' }
    ];
  }

  selectRisk(risk: any): void {
    this.editingRiskId = risk.id;
    this.form = {
      title: risk.title || '',
      description: risk.description || '',
      severity: risk.severity || 'Medium',
      status: risk.status || 'Open',
      mitigationPlan: risk.mitigationPlan || '',
      ownerStaffId: risk.ownerStaffId || null,
      targetResolutionDate: this.formatDateInput(risk.targetResolutionDate),
      mentionedStaffIds: []
    };
  }

  resetForm(): void {
    this.editingRiskId = null;
    this.form = {
      title: '',
      description: '',
      severity: 'Medium',
      status: 'Open',
      mitigationPlan: '',
      ownerStaffId: null,
      targetResolutionDate: '',
      mentionedStaffIds: []
    };
  }

  saveRisk(): void {
    if (!this.selectedProjectId) return;
    if (!this.form.title.trim()) {
      Swal.fire('Validation', 'Risk title is required.', 'warning');
      return;
    }

    this.saving = true;
    const payload = {
      title: this.form.title.trim(),
      description: this.form.description || null,
      severity: this.form.severity,
      status: this.form.status,
      mitigationPlan: this.form.mitigationPlan || null,
      ownerStaffId: this.form.ownerStaffId,
      targetResolutionDate: this.form.targetResolutionDate || null,
      mentionedStaffIds: this.form.mentionedStaffIds
    };

    const request = this.editingRiskId
      ? this.projectService.updateProjectRisk(this.editingRiskId, payload)
      : this.projectService.createProjectRisk(this.selectedProjectId, payload);

    request.subscribe({
      next: () => {
        this.saving = false;
        Swal.fire('Success', this.editingRiskId ? 'Risk updated.' : 'Risk created.', 'success');
        this.resetForm();
        this.loadProjectRisks();
      },
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to save risk.', 'error');
      }
    });
  }

  isLate(risk: any): boolean {
    if (!risk?.targetResolutionDate) return false;
    const done = ['mitigated', 'closed'].includes(String(risk.status || '').toLowerCase());
    return !done && new Date(risk.targetResolutionDate).getTime() < Date.now();
  }

  private countByStatus(status: string): number {
    return this.risks.filter((risk) => String(risk.status || '').toLowerCase() === status.toLowerCase()).length;
  }

  private countBySeverity(severity: string): number {
    return this.risks.filter((risk) => String(risk.severity || '').toLowerCase() === severity.toLowerCase()).length;
  }

  private formatDateInput(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  }
}
